import { Button, ButtonGroup, Divider } from '@aon/aon-tws-common-ui';
import * as React from 'react';
import _ from 'lodash';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { useAppSelector } from '../../../appStore';
import ContentConstants from '../../../contentConstants';
import { DistributionType, IAudience, SurveyStatusType } from '../../../models';
import { EmailTemplatePropertiesUnderValidation } from '../../../models/EmailTemplatePropertiesUnderValidation';
import { Status } from '../../../models/Status';
import { IApplicationState } from '../../../store';
import { defaultAudience } from '../../../store/AudienceDetailsStore';
import { addEmailTemplate, updateEmailTemplate } from '../../../store/EmailTemplateSlice';
import { useAudienceEmails } from '../../../utilities/hooks/useAudienceEmails';
import * as AudienceValidation from '../../audience/AudienceValidation';
import { InviteEmail } from '../../audience/email/InviteEmail';
import { CardLayout, SaveButtonsContainer } from '../../common';

export const AdminInviteEmail = (props: { surveyId: number; audienceId: number }) => {
	const { audienceId, surveyId } = props;
	const { audiences } = useAppSelector((state) => state.surveyAdmin);
	const audience = audiences.find((a: IAudience) => a.audienceId === audienceId) ?? defaultAudience;
	const {
		defaultEmailTemplates,
		invitationEmail,
		invitationEmailValidationErrors,
		isMultiLanguageSurvey,
		isTemplateWarningModalOpen,
		selectedDefaultTemplate,
		sendEmailModalOpen,
		AddInvitationEmailValidationErrors,
		CloseTemplateWarningModal,
		SendEmail,
		SelectDefaultTemplate,
		SubmitTemplateWarningModal,
		Toast,
		ToggleSendEmailModal,
		UpdateInvitationEmail,
	} = useAudienceEmails(audienceId, surveyId);

	const dispatch = useDispatch();
	const { status } = useSelector((state: IApplicationState) => state.emailTemplates);
	const { isLoadingAudiences } = useSelector((state: IApplicationState) => state.surveyAdmin);
	const surveyAdminSummary = useSelector((state: IApplicationState) => state.surveyAdmin.surveyAdminSummary);
	let isReadOnly = surveyAdminSummary ? surveyAdminSummary.surveyStatus === SurveyStatusType.Cancelled : false;
	if (!isReadOnly) {
		isReadOnly = audience?.openOn ? moment(audience.openOn).isBefore(new Date()) : false;
	}

	const validateInvitationEmail = (distributionType?: DistributionType, fieldNames?: EmailTemplatePropertiesUnderValidation[]) => {
		let inviteEmailValidationErrors = AudienceValidation.validEmailTemplateFields(
			fieldNames || [
				EmailTemplatePropertiesUnderValidation.From,
				EmailTemplatePropertiesUnderValidation.ReplyToEmail,
				EmailTemplatePropertiesUnderValidation.Subject,
				EmailTemplatePropertiesUnderValidation.Content,
				EmailTemplatePropertiesUnderValidation.SurveyLink,
			],
			invitationEmail,
			distributionType,
		);

		// Remove survey link validation error
		if (
			fieldNames &&
			fieldNames.length === 1 &&
			fieldNames[0] === EmailTemplatePropertiesUnderValidation.SurveyLink &&
			invitationEmailValidationErrors
		) {
			inviteEmailValidationErrors = _.omit(invitationEmailValidationErrors, [
				EmailTemplatePropertiesUnderValidation.Content,
				EmailTemplatePropertiesUnderValidation.SurveyLink,
			]);
		}
		AddInvitationEmailValidationErrors(inviteEmailValidationErrors);
		return inviteEmailValidationErrors;
	};

	const handleSave = () => {
		const valid = validateInvitationEmail();
		if (!valid) {
			const inviteEmail = { ...invitationEmail, audienceId: audienceId };
			if (invitationEmail.emailTemplateId === 0) {
				dispatch(addEmailTemplate({ surveyId, emailTemplate: inviteEmail }));
			} else {
				dispatch(updateEmailTemplate({ surveyId, emailTemplate: inviteEmail }));
			}
		}
	};

	return (
		<CardLayout
			loading={status === Status.Loading || isLoadingAudiences}
			title={isReadOnly ? ContentConstants.SURVEY_ADMIN_ACTION_INVITE_EMAIL_READONLY : ContentConstants.SURVEY_ADMIN_ACTION_INVITE_EMAIL}
		>
			<InviteEmail
				audience={audience}
				defaultEmailTemplates={defaultEmailTemplates}
				invitationEmail={invitationEmail}
				isMultiLanguageSurvey={isMultiLanguageSurvey}
				inviteEmailValidationError={invitationEmailValidationErrors}
				isTemplateWarningModalOpen={isTemplateWarningModalOpen}
				selectedDefaultTemplate={selectedDefaultTemplate}
				readOnly={isReadOnly}
				sendEmailModalOpen={sendEmailModalOpen}
				CloseTemplateWarningModal={CloseTemplateWarningModal}
				onChange={UpdateInvitationEmail}
				toast={Toast}
				selectDefaultTemplate={SelectDefaultTemplate}
				sendEmail={SendEmail}
				submitTemplateWarningModal={SubmitTemplateWarningModal}
				toggleSendEmailModal={ToggleSendEmailModal}
				validateInvitationEmail={validateInvitationEmail}
				shouldDisableTestEmail={false}
				isAdminMenu={true}
				surveyId={surveyId}
			/>
			{!isReadOnly && (
				<>
					{' '}
					<Divider type='dashed' />
					<SaveButtonsContainer>
						<ButtonGroup type='column'>
							<Button
								id='SaveChangesButton'
								color='primary'
								size='lg'
								disabled={status === Status.Saving}
								onClick={handleSave}
								aria-label={'Save Changes'}
							>
								{ContentConstants.BUTTON_SAVE_CHANGES}
							</Button>
						</ButtonGroup>
					</SaveButtonsContainer>
				</>
			)}
		</CardLayout>
	);
};
